import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { api } from "@/lib/api";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription, SheetTrigger } from "@/components/ui/sheet";
import { HelpCircle, Loader2, ChevronRight } from "lucide-react";

/**
 * Renders a floating "?" button that opens a drawer with MANUAL.md sections
 * contextual to the current page (based on `/app/{page}` URL segment).
 */

function segmentFromPath(pathname) {
  // "/app/clients/xyz" -> "clients"
  const parts = pathname.split("/").filter(Boolean);
  if (parts[0] === "app") return parts[1] || "dashboard";
  return parts[0] || "";
}

// Minimal markdown-to-html without pulling any lib
function renderMarkdown(md) {
  if (!md) return null;
  const lines = md.split("\n");
  const out = [];
  let inList = false;
  let listBuf = [];
  const flushList = () => {
    if (inList) {
      out.push(<ul key={out.length} className="list-disc pl-5 my-2 space-y-1 text-sm text-neutral-700">{listBuf}</ul>);
      inList = false; listBuf = [];
    }
  };
  lines.forEach((ln, i) => {
    if (/^\s*[-*]\s+/.test(ln)) {
      inList = true;
      listBuf.push(<li key={i}>{ln.replace(/^\s*[-*]\s+/, "")}</li>);
      return;
    }
    flushList();
    const h3 = ln.match(/^###\s+(.+)/);
    const h4 = ln.match(/^####\s+(.+)/);
    if (h3) { out.push(<h4 key={i} className="text-sm font-semibold mt-4 mb-1.5 text-neutral-900">{h3[1]}</h4>); return; }
    if (h4) { out.push(<h5 key={i} className="text-xs font-semibold mt-3 mb-1 text-neutral-700 uppercase tracking-wider">{h4[1]}</h5>); return; }
    if (!ln.trim()) { out.push(<div key={i} className="h-2" />); return; }
    out.push(<p key={i} className="text-sm text-neutral-700 leading-relaxed">{ln}</p>);
  });
  flushList();
  return out;
}

export default function HelpDrawer() {
  const location = useLocation();
  const page = segmentFromPath(location.pathname);
  const [open, setOpen] = useState(false);
  const [sections, setSections] = useState([]);
  const [selected, setSelected] = useState(null);
  const [loading, setLoading] = useState(false);
  const [allOpen, setAllOpen] = useState(false);
  const [all, setAll] = useState([]);

  useEffect(() => {
    if (!open) return;
    setLoading(true);
    api.get(`/help/for-page`, { params: { page } })
      .then(({ data }) => {
        setSections(data.sections || []);
        setSelected((data.sections || [])[0] || null);
      })
      .finally(() => setLoading(false));
  }, [open, page]);

  useEffect(() => {
    if (!allOpen) return;
    api.get(`/help/sections`).then(({ data }) => setAll(data));
  }, [allOpen]);

  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetTrigger asChild>
        <button
          className="fixed bottom-6 right-6 z-40 h-11 w-11 rounded-full bg-neutral-900 text-white grid place-items-center shadow-lg hover:bg-neutral-800"
          data-testid="help-drawer-trigger"
          aria-label="Open help"
        >
          <HelpCircle className="w-5 h-5" />
        </button>
      </SheetTrigger>
      <SheetContent side="right" className="w-full sm:w-[520px] p-0 flex flex-col" data-testid="help-drawer">
        <SheetHeader className="px-6 py-4 border-b border-neutral-200">
          <SheetTitle>Help · <span className="text-neutral-500 font-normal">{page}</span></SheetTitle>
          <SheetDescription>
            Contextual answers from the manual.{" "}
            <Link to="/app/manual" onClick={() => setOpen(false)} className="underline text-neutral-900" data-testid="help-open-full-manual">Open full manual →</Link>
          </SheetDescription>
        </SheetHeader>
        <div className="flex-1 overflow-y-auto">
          {loading ? (
            <div className="p-8 grid place-items-center"><Loader2 className="w-5 h-5 animate-spin text-neutral-400" /></div>
          ) : sections.length === 0 && !allOpen ? (
            <div className="p-6 text-sm text-neutral-500">
              <p>No section explicitly tagged for this page.</p>
              <button className="mt-3 text-neutral-900 underline text-sm" onClick={() => setAllOpen(true)} data-testid="help-open-all">Browse all sections →</button>
            </div>
          ) : allOpen ? (
            <div className="p-4 space-y-1" data-testid="help-all-sections">
              {all.map((s) => (
                <button
                  key={s.id}
                  onClick={() => { setSelected(s); setAllOpen(false); }}
                  className="w-full text-left p-3 rounded-lg hover:bg-neutral-50 flex items-start justify-between gap-3"
                >
                  <div>
                    <div className="text-sm font-medium text-neutral-900">{s.title}</div>
                    <div className="text-xs text-neutral-500 line-clamp-2">{s.excerpt}</div>
                  </div>
                  <ChevronRight className="w-4 h-4 text-neutral-400 shrink-0 mt-1" />
                </button>
              ))}
            </div>
          ) : (
            <div className="flex h-full">
              <aside className="w-44 shrink-0 border-r border-neutral-200 py-3 overflow-y-auto">
                {sections.map((s) => (
                  <button
                    key={s.id}
                    onClick={() => setSelected(s)}
                    className={`block w-full text-left px-4 py-2 text-xs ${selected?.id === s.id ? "bg-neutral-100 font-semibold text-neutral-900" : "text-neutral-600 hover:bg-neutral-50"}`}
                    data-testid={`help-section-${s.id}`}
                  >
                    {s.title}
                  </button>
                ))}
                <button className="block w-full text-left px-4 py-2 text-xs text-neutral-500 hover:bg-neutral-50 mt-3" onClick={() => setAllOpen(true)}>
                  All sections →
                </button>
              </aside>
              <div className="flex-1 p-5 overflow-y-auto" data-testid="help-content">
                {selected ? (
                  <>
                    <h3 className="text-base font-semibold mb-3">{selected.title}</h3>
                    {renderMarkdown(selected.body_markdown)}
                  </>
                ) : (
                  <div className="text-sm text-neutral-500">Pick a section on the left.</div>
                )}
              </div>
            </div>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
}
